import { get, isFunction } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { constants } from '@clubmed/utils';
import { Button } from 'Components/frameworks/button/Button';
import { Icon } from 'Components/frameworks/icon/Icon';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { ICONS } from 'Components/utils/icon/icons';
import { CriteriaContext } from 'Components/widgets/criteria/CriteriaContext';
import { useCriteriaDisplayContext } from 'Components/widgets/criteria/CriteriaDisplayContext';
import { CriteriaQuery } from 'Components/widgets/criteria/criteria-query/CriteriaQuery';
import { CriteriaTransport } from 'Components/widgets/criteria/criteria-transport/CriteriaTransport';
import { CriteriaWhen } from 'Components/widgets/criteria/criteria-when/CriteriaWhen';
import { CriteriaWho } from 'Components/widgets/criteria/criteria-who/CriteriaWho';
import { CriteriaModalWhen } from 'Components/widgets/criteria-modal/criteria-modal-when/CriteriaModalWhen';
import { useTrackQueryChange } from 'Components/widgets/facet-search/useTrackQueryChange';
import { useAppContext } from 'Context/ReactAppContext';
import { t, addTranslationKeys } from 'Src/i18n';
import { facetsSelector, filteredProductsCountSelector } from 'Store/faceting/faceting.selector';
import { setIsSearchEnabled } from 'Store/faceting/facetingSearch.action';
import { setProductComparisonSelectionMode } from 'Store/faceting/productComparison.action';
import { DURATION_FACET } from 'Utils/faceting/constants';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { isChineseLocale } from 'Utils/locale/localeUtils';
import { classnames } from 'Utils/classnames/classnames.utils';
import { getDepartureCitiesFromFacets } from './SearchForm.utils';
const { PRODUCT_TYPES } = constants;

const SubmitButton = ({ className, dataTestId }: { className?: string; dataTestId?: string }) => (
  <div className={className}>
    <Button className="w-48 leading-0" data-testid={dataTestId} isSquare type="submit">
      <Icon name={ICONS.SEARCH_GLASS} width="1.5rem" />
    </Button>
  </div>
);

export type Props = {
  className?: string;
  doTrackQueryChange: boolean;
  fullScreen?: boolean;
  i18n?: CMSContent;
  layerButtonLabel?: string;
  onSubmit?: (event?: React.FormEvent<HTMLFormElement>) => void;
  showNbResultsInLayer?: boolean;
  withFacets?: boolean;
  withQuery?: boolean;
};
export const SearchForm = (props: Props) => {
  const appContext = useAppContext();
  const [, setCriteriaContext] = useContext(CriteriaContext);
  const [criteriaDisplayContext] = useCriteriaDisplayContext();
  const [criteriaQueryIsJoyviewProduct, setCriteriaQueryIsJoyviewProduct] = useState(false);
  const [criteriaModalWhenIsOpen, setCriteriaModalWhenIsOpen] = useState(false);
  const trackQueryChange = useTrackQueryChange();
  const dispatch = useDispatch();
  const departureDate: moment$Moment | null | undefined = useSelector(
    (state) => state?.tripCriteria?.departureDate,
  );
  const duration: number = useSelector((state) => get(state, 'tripCriteria.duration', 0));
  const facets: Array<any> = useSelector((state) => facetsSelector(state));
  const resultsCount: number = useSelector((state) => filteredProductsCountSelector(state));
  const isLoading: boolean = useSelector((state) => state.faceting.isLoading);
  const isProductComparisonSelectionModeEnabled: boolean = useSelector(
    (state) => state?.faceting?.isSelectionModeEnabled,
  );
  const joyviewOffsetSell: number = useSelector((state) => state.faceting.joyviewOffsetSell);
  const resortOffsetSell: number = useSelector((state) => state.faceting.resortOffsetSell);
  const b2cOffsetSell =
    isChineseLocale(appContext.locale) && criteriaQueryIsJoyviewProduct && joyviewOffsetSell > -1
      ? joyviewOffsetSell
      : resortOffsetSell;
  const offsetSell = appContext.isSeller ? 0 : b2cOffsetSell;

  const getOptionsDuration = () => {
    const results = (
      facets.find((f) => f.id === DURATION_FACET) || {
        list: [],
      }
    ).list;
    return results.map((opt) => parseInt(opt.value, 10)).sort((a, b) => (a < b ? -1 : 1));
  };

  useEffect(() => {
    setCriteriaContext({
      layerButtonLabel: props.layerButtonLabel,
      layerHeader: props.showNbResultsInLayer
        ? t('faceting.viewResorts', {
            n: `<span class="font-bold text-sienna">${resultsCount || 0}</span>`,
          })
        : '',
    });
  }, [props.showNbResultsInLayer, resultsCount, props.layerButtonLabel]);
  useEffect(() => {
    if (
      criteriaDisplayContext.criteriaWhenIsOpen ||
      criteriaDisplayContext.criteriaTransportIsOpen
    ) {
      dispatch(setIsSearchEnabled(true));
    }
  }, [criteriaDisplayContext.criteriaTransportIsOpen, criteriaDisplayContext.criteriaWhenIsOpen]);

  const updateTranslationKeys = () => {
    addTranslationKeys({
      common: get(props, 'i18n.common'),
      booking: get(props, 'i18n.booking'),
      faceting: get(props, 'i18n.faceting'),
    });
  };

  updateTranslationKeys();
  useEffect(() => {
    updateTranslationKeys();
  }, [props.i18n]);
  const isOpen =
    criteriaDisplayContext.criteriaWhenIsOpen ||
    criteriaDisplayContext.criteriaWhoIsOpen ||
    criteriaDisplayContext.criteriaTransportIsOpen;
  useEffect(() => {
    if (isOpen) {
      scrollToElement('#search-form', 250);
    }
  }, [isOpen]);
  useEffect(() => {
    const dateIsMissing = !departureDate || !duration;

    if (
      isProductComparisonSelectionModeEnabled &&
      dateIsMissing &&
      !criteriaDisplayContext.criteriaWhenIsOpen &&
      !criteriaModalWhenIsOpen
    ) {
      setCriteriaModalWhenIsOpen(true);
    }
  }, [
    criteriaDisplayContext.criteriaWhenIsOpen,
    isProductComparisonSelectionModeEnabled,
    departureDate,
  ]);
  const departureCitiesOptions = getDepartureCitiesFromFacets(facets, null);
  const hideCriteriaTransport = isFeatureFlipped(appContext)('faceting.hideCriteriaTransport');

  const onSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    if (event) {
      event.preventDefault();
    }

    if (isFunction(props.onSubmit)) {
      props.onSubmit(event);
    }
  };

  const onQueryChange = (query) => {
    setCriteriaQueryIsJoyviewProduct(query && query.productType === PRODUCT_TYPES.JOYVIEW);

    if (query && query.label && query.type) {
      return props.doTrackQueryChange && trackQueryChange(query);
    }
  };

  const onClose = () => {
    setCriteriaModalWhenIsOpen(false);

    if (!departureDate && !duration) {
      dispatch(setProductComparisonSelectionMode(false));
    }
  };

  const durations = getOptionsDuration();

  const onCriteriaQueryFocus = () => {
    scrollToElement('#search-form', 250);
  };

  return (
    <form className={classnames('relative isolate', props.className)} onSubmit={onSubmit}>
      <SiteContainer isWide={props.fullScreen}>
        <div
          className={classnames('z-8 border-b border-lightSand bg-white p-5 lg:flex', {
            'mb-20 rounded-24 border-1 shadow-lg lg:rounded-pill': !props.fullScreen,
          })}
          id="search-form"
        >
          {props.withQuery && (
            <div className="flex gap-10 p-5 lg:flex-1">
              <div className="relative z-1 flex-1" data-testid="search-form-criteria-query">
                <CriteriaQuery
                  onFocus={onCriteriaQueryFocus}
                  onQueryChange={onQueryChange}
                  suggestedRequests={props.i18n?.faceting?.suggestedRequests}
                />
              </div>
              <SubmitButton className="lg:hidden" dataTestId="facet-search-button-mobile" />
            </div>
          )}
          <div className="flex lg:flex-2">
            <div className="flex-1 p-5" data-testid="search-form-criteria-when">
              <CriteriaWhen
                durationOptions={durations}
                offsetSell={offsetSell > -1 ? offsetSell : null}
                isDropDownRelative={props.fullScreen}
                withFlexibility
                theme="compact"
              />
            </div>
            <div className="flex-1 p-5" data-testid="search-form-criteria-who">
              <CriteriaWho />
            </div>
            {!hideCriteriaTransport && (
              <div className="flex-1 p-5" data-testid="search-form-criteria-transport">
                <CriteriaTransport
                  departureCitiesOptions={departureCitiesOptions}
                  isLoading={isLoading}
                />
              </div>
            )}
          </div>
          <SubmitButton className="m-5 hidden lg:block" dataTestId="facet-search-button" />
        </div>
      </SiteContainer>
      {criteriaModalWhenIsOpen && (
        <CriteriaModalWhen durationOptions={durations} onClose={onClose} />
      )}
    </form>
  );
};
